import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Brush, ReferenceLine
} from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle, Activity } from 'lucide-react';
import PageHeader from '@/components/PageHeader';

const sampleData = [
  { date: '2024-01-01', bod: 12000, cod: 28000, tss: 6000, ammonia: 800, tn: 1100, tp: 120 },
  { date: '2024-02-01', bod: 11500, cod: 26500, tss: 5800, ammonia: 780, tn: 1080, tp: 115 },
  { date: '2024-03-01', bod: 10800, cod: 25000, tss: 5200, ammonia: 750, tn: 1050, tp: 110 },
  { date: '2024-04-01', bod: 13200, cod: 31000, tss: 7100, ammonia: 900, tn: 1200, tp: 135 },
  { date: '2024-05-01', bod: 12500, cod: 29000, tss: 6500, ammonia: 850, tn: 1150, tp: 128 },
  { date: '2024-06-01', bod: 9800, cod: 22000, tss: 4800, ammonia: 700, tn: 1000, tp: 105 },
  { date: '2024-07-01', bod: 10500, cod: 24000, tss: 5100, ammonia: 720, tn: 1020, tp: 108 },
  { date: '2024-08-01', bod: 11200, cod: 26000, tss: 5500, ammonia: 760, tn: 1060, tp: 112 },
  { date: '2024-09-01', bod: 11800, cod: 27500, tss: 5900, ammonia: 790, tn: 1090, tp: 118 },
  { date: '2024-10-01', bod: 10200, cod: 23500, tss: 5000, ammonia: 710, tn: 1010, tp: 106 },
];

const parameters = [
  { key: 'bod', name: 'BOD', color: '#06b6d4', limit: 3000 },
  { key: 'cod', name: 'COD', color: '#8b5cf6', limit: 5000 },
  { key: 'tss', name: 'TSS', color: '#f59e0b', limit: 400 },
  { key: 'ammonia', name: 'Ammonia', color: '#10b981', limit: 45 },
  { key: 'tn', name: 'Total N', color: '#f97316', limit: 50 },
  { key: 'tp', name: 'Total P', color: '#ec4899', limit: 10 },
];

function StatCard({ label, value, unit, trend, trendValue }: { label: string; value: string; unit: string; trend: 'up' | 'down' | 'flat'; trendValue: string }) {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Activity;
  const trendColor = trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-slate-500';
  return (
    <div className="rb-card p-4">
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-white tabular-nums">{value}</span>
        <span className="text-xs text-slate-500">{unit}</span>
      </div>
      <div className={`flex items-center gap-1 mt-1 ${trendColor}`}>
        <TrendIcon className="w-3 h-3" />
        <span className="text-xs">{trendValue}</span>
      </div>
    </div>
  );
}

export default function TrendsPage() {
  const [activeParams, setActiveParams] = useState<string[]>(['bod', 'cod', 'tss']);

  const toggleParam = (key: string) => {
    setActiveParams(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
  };

  return (
    <div className="space-y-6">
      <PageHeader icon={TrendingUp} title="Parameter Trends" subtitle="Multi-sample water quality trend analysis" />
      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Avg BOD" value="11,320" unit="mg/L" trend="down" trendValue="-3.2% vs last month" />
        <StatCard label="Avg COD" value="26,050" unit="mg/L" trend="down" trendValue="-1.8% vs last month" />
        <StatCard label="Avg TSS" value="5,580" unit="mg/L" trend="flat" trendValue="No significant change" />
        <StatCard label="Samples" value="10" unit="total" trend="up" trendValue="+2 this quarter" />
      </div>

      {/* Main Chart */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rb-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Multi-Sample Parameter Trends</h3>
          <div className="flex flex-wrap gap-2">
            {parameters.map(p => (
              <button
                key={p.key}
                onClick={() => toggleParam(p.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  activeParams.includes(p.key)
                    ? 'text-white border-slate-600'
                    : 'text-slate-500 border-slate-800 opacity-50'
                }`}
                style={activeParams.includes(p.key) ? { backgroundColor: p.color + '20', borderColor: p.color + '40' } : {}}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                {p.name}
              </button>
            ))}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={sampleData}>
            <defs>
              {parameters.map(p => (
                <linearGradient key={p.key} id={`grad-${p.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={p.color} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={p.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="date" stroke="#64748b" tick={{ fill: 'var(--rb-text-3)', fontSize: 11 }} />
            <YAxis stroke="#64748b" tick={{ fill: 'var(--rb-text-3)', fontSize: 11 }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', borderRadius: '0.5rem', color: 'var(--rb-text)' }}
            />
            {parameters.filter(p => activeParams.includes(p.key)).map(p => (
              <Area key={p.key} type="monotone" dataKey={p.key} name={p.name} stroke={p.color} strokeWidth={2} fill={`url(#grad-${p.key})`} />
            ))}
            {parameters.filter(p => activeParams.includes(p.key)).map(p => (
              <ReferenceLine key={`limit-${p.key}`} y={p.limit} stroke={p.color} strokeDasharray="6 3" strokeOpacity={0.5} label={{ value: `${p.name} Limit`, fill: p.color, fontSize: 10 }} />
            ))}
            <Brush dataKey="date" height={30} stroke="#334155" fill="#0f172a" travellerWidth={8} />
          </AreaChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Anomaly Alert */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="rb-card p-4 border-l-4 border-l-amber-500">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-300">Anomaly Detected</p>
            <p className="text-xs text-slate-400">Sample from April 2024 shows 22% deviation from BOD trend. Review recommended.</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
